import React from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { useQuery } from "react-query";
import { communication } from "../../constants/apis/communication";
import "./StackedBarCard.css";

const COLORS = ["#3a57e8", "#ff958d", "#aaff75", "#f5a623", "#9013fe", "#00bcd4", "#ffeb3b", "#e91e63"];

// Custom Tooltip for Area Chart
const CustomAreaTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip-premium" style={{ backgroundColor: "#ffffff", padding: "12px", borderRadius: "8px", color: "#333", border: "1px solid #eaeaea", boxShadow: "0 4px 15px rgba(0,0,0,0.1)" }}>
        <p className="label" style={{ fontSize: "12px", color: "#666", marginBottom: "8px", fontWeight: "bold" }}>{`Date: ${label}`}</p>
        <div className="tooltip-items">
          {payload.map((item, index) => (
            <div key={index} className="tooltip-item d-flex align-items-center gap-2 mb-1">
              <span className="tooltip-indicator" style={{ backgroundColor: item.color || item.fill, width: "10px", height: "10px", borderRadius: "50%", display: "inline-block" }}></span>
              <span className="name text-capitalize" style={{ fontSize: "13px" }}>{item.name}:</span>
              <span className="value font-weight-bold" style={{ fontSize: "14px" }}>{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

// Generate fallback mock data if DB is empty
const generateMockData = () => {
  const data = [];
  const categories = ["compressor (Air Compressor)", "loader (Wheel Loader)", "dumper (Heavy Dumper)"];
  for (let i = 29; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    data.push({
      date: d.toISOString().split("T")[0],
      "compressor (Air Compressor)": Math.floor(Math.random() * 40) + 10,
      "loader (Wheel Loader)": Math.floor(Math.random() * 30) + 5,
      "dumper (Heavy Dumper)": Math.floor(Math.random() * 50) + 15,
    });
  }
  return { data, categories };
};

export default function StackedBarCard() {
  const { data: chartResponse, isLoading } = useQuery(
    ["getLogChartData"],
    () => communication.getLogChartData({}),
    { refetchOnWindowFocus: false }
  );

  const mockFallback = generateMockData();
  const hasRealData = chartResponse?.data?.data && chartResponse.data.data.length > 0;
  
  const chartData = hasRealData ? chartResponse.data.data : mockFallback.data;
  const categories = hasRealData ? chartResponse.data.categories : mockFallback.categories;

  return (
    <div className="card-premium p-4 d-flex flex-column justify-content-between relative" style={{ minHeight: "360px" }}>
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
        <h4 className="chart-card-title">Log Reports (Last 30 Days)</h4>
        <div className="d-flex align-items-center gap-3">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            {categories.map((cat, i) => (
              <div key={cat} className="d-flex align-items-center gap-2">
                <span className="legend-dot" style={{ backgroundColor: COLORS[i % COLORS.length] }}></span>
                <span style={{ fontSize: "13px", color: "#666", fontWeight: "500", textTransform: "capitalize" }}>{cat}</span>
              </div>
            ))}
          </div>
          <div className="legend-outline-box"></div>
        </div>
      </div>

      <div className="chart-render-container flex-1">
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center h-100 w-100" style={{ color: "#666" }}>
            Loading chart data...
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
              <defs>
                {categories.map((cat, i) => (
                  <linearGradient key={`color-${cat}`} id={`color-${cat.replace(/[^a-zA-Z0-9]/g, "_")}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0.6} />
                    <stop offset="95%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0.0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" vertical={false} />
              <XAxis 
                dataKey="date" 
                tickLine={false} 
                axisLine={false} 
                tick={{ fill: "#666", fontSize: 12, fontWeight: 600 }} 
                dy={10} 
                tickFormatter={(val) => {
                  // Optional: shorten date from YYYY-MM-DD to DD-MM
                  const parts = val.split('-');
                  return parts.length === 3 ? `${parts[2]}/${parts[1]}` : val;
                }}
              />
              <YAxis 
                tickLine={false} 
                axisLine={false} 
                tick={{ fill: "#666", fontSize: 12, fontWeight: 600 }} 
                dx={-5} 
              />
              <Tooltip content={<CustomAreaTooltip />} cursor={{ stroke: 'rgba(0,0,0,0.1)', strokeWidth: 1, strokeDasharray: '3 3' }} />
              {categories.map((cat, i) => (
                <Area
                  key={cat}
                  type="monotone"
                  dataKey={cat}
                  stroke={COLORS[i % COLORS.length]}
                  strokeWidth={3}
                  fillOpacity={1}
                  fill={`url(#color-${cat.replace(/[^a-zA-Z0-9]/g, "_")})`}
                  activeDot={{ r: 6, fill: COLORS[i % COLORS.length], stroke: '#fff', strokeWidth: 2 }}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
